/**
 * Restricted modules (VAW Desk, BADAC, Health): who may open them. Only the
 * Punong Barangay, or someone who already holds a module, can give or remove
 * it; nobody changes their own; the administrator has none by default.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Card, PageHeader, Skeleton, TableWrap, Td, Th } from '../components/ui';

interface GrantsView {
  modules: { key: string; label: string }[];
  people: { membershipId: string; name: string; roleName: string; grants: string[] }[];
}

export function RestrictedAccessPage(): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['grants'], queryFn: () => api<GrantsView>('/grants') });
  const save = useMutation({
    mutationFn: ({ membershipId, grants }: { membershipId: string; grants: string[] }) => api(`/users/${membershipId}/grants`, { method: 'PATCH', body: { grants } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['grants'] }),
  });
  const isPunong = user?.roleName === 'Punong Barangay';
  const mayChange = (key: string) => isPunong || Boolean(user?.grants?.includes(key));

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Restricted access" description="VAW Desk, BADAC and Health records are confidential. Access is given to one person at a time and every view is logged. Roles never include it." />
      <div className="mb-4"><Alert tone="info">{isPunong ? 'As Punong Barangay you can give or remove access. You cannot change your own.' : 'You can share or remove only the modules you already hold. You cannot change your own access.'} A change signs the person out so it applies at once.</Alert></div>
      {save.error && <div className="mb-4"><Alert tone="critical">{save.error.message}</Alert></div>}
      {data.isLoading || !data.data ? <Skeleton className="h-64 w-full" /> : (
        <Card>
          <TableWrap>
            <thead><tr><Th>Person</Th>{data.data.modules.map((m) => <Th key={m.key}><span className="inline-flex items-center gap-1"><Lock aria-hidden="true" className="h-3 w-3" />{m.label.split(' (')[0]}</span></Th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.data.people.map((p) => {
                const self = p.name === user?.name;
                return (
                  <tr key={p.membershipId}>
                    <Td><div className="font-medium">{p.name}</div><div className="text-xs text-slate-500">{p.roleName}</div></Td>
                    {data.data.modules.map((m) => (
                      <Td key={m.key}>
                        <input type="checkbox" className="h-5 w-5 cursor-pointer disabled:cursor-not-allowed" aria-label={`${m.label} for ${p.name}`}
                          checked={p.grants.includes(m.key)} disabled={self || !mayChange(m.key) || save.isPending}
                          onChange={(e) => {
                            const next = e.target.checked ? [...p.grants, m.key] : p.grants.filter((g) => g !== m.key);
                            if (window.confirm(`${e.target.checked ? 'Give' : 'Remove'} ${m.label.split(' (')[0]} ${e.target.checked ? 'to' : 'from'} ${p.name}?`)) save.mutate({ membershipId: p.membershipId, grants: next });
                          }} />
                      </Td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}
